import {FC, useContext, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {toast} from 'react-toastify';
//redux actions
import {
  changeProfile,
  organizationAppleSignup,
  organizationGoogleSignup,
  organizationSignup,
} from '@/store/organization/actions';
//formik
import {Formik} from 'formik';
//form configuration
import {SignUpFormValues, getValidationSchema, initialValues} from './formConfiguration';
//mui
import {Box, Typography} from '@mui/material';
//icons
import {ReactComponent as CheckIcon} from '@/assets/icons/rudiobutton-checked.svg';
//context
import {OrganizationSignUpContext} from '@/pages/Organization/OrganizationSignUp/OrganizationSignUpContext';
//hooks
import {useAppDispatch, useAppSelector} from '@/store/hooks';
//constants
import {BODY2, DONE, FILLED} from '@/utils/constants';
//enums
import {ROUTER_URL, USER_TYPE} from '@/utils/enums';
//components
import Stepper from '@/shared/components/Stepper';
import Modal from '@/shared/components/Modal';
import SignUpFormContent from './SignUpFormContent';
import LogoFormContent from './LogoFormContent';
import PaymentContent from './PaymentContent';
//helpers
import {SIGNUP_STEP} from './helpers';
import {getErrorMessage} from '@/utils/helpers';
//styles
import {formStepperStyle} from './FormStepper.styles';
import {OrganizationService, orgStorage} from '@/utils/services';
import {organizationUpdateNotificationSettings} from '@/store/organization-notificationSetting';
import {organizationNotificationSettingsInitialData} from '@/utils/constants/notificationSettings';
import {sendPresignedImage} from '@/pages/Organization/Profile/General/ChangePhotoModal';
import {orgAPI} from '@/core/api';
import {getOrganizationIdSelector, getOrganizationSelector} from '@/store/organization';
import {IAppleSignupSession, IAuthAppleRequest} from '@/utils/types';

type SocialSignup = {provider: 'Google'; idToken: string; email: string} | ({provider: 'Apple'} & IAppleSignupSession);

export const getImage = async (logo: File) => {
  const presignedLogoUrl = await OrganizationService.presignOrganizationAvatar(orgAPI);

  await sendPresignedImage(presignedLogoUrl.data.url, logo);
  return presignedLogoUrl.data.key.split('/').pop();
};

const FormStepper: FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const organizationId = useAppSelector(getOrganizationIdSelector);
  const organization = useAppSelector(getOrganizationSelector);

  const {activeStep, nextStepHandler} = useContext(OrganizationSignUpContext);

  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [socialSignup, setSocialSignup] = useState<SocialSignup | null>(null);

  const onOpenSuccessModal = () => navigate(`${ROUTER_URL.ORGANIZATION}`);

  const handleGoogleSignupSuccess = (idToken: string) => {
    let email = '';

    try {
      const payload = idToken.split('.')[1];
      const base64 = payload
        .replace(/-/g, '+')
        .replace(/_/g, '/')
        .padEnd(Math.ceil(payload.length / 4) * 4, '=');
      const decodedPayload = JSON.parse(atob(base64));
      email = typeof decodedPayload.email === 'string' ? decodedPayload.email : '';
    } catch {
      email = '';
    }

    setSocialSignup({provider: 'Google', idToken, email});
  };

  const handleAppleSignupSuccess = async (request: IAuthAppleRequest) => {
    try {
      const {data} = await OrganizationService.prepareAppleSignup(request);
      setSocialSignup({provider: 'Apple', ...data});
    } catch (error) {
      if (getErrorMessage(error) === 'EmailAlreadyExists') {
        throw new Error('An account already exists. Please log in instead.');
      }
      throw error;
    }
  };

  const setNotificationSettings = async (id: number) => {
    try {
      await dispatch(
        organizationUpdateNotificationSettings({
          id,
          reqBody: organizationNotificationSettingsInitialData,
        })
      ).unwrap();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleSubmitClickHandler = async (values: SignUpFormValues) => {
    if (activeStep === SIGNUP_STEP.FORM) {
      try {
        const {name, taxIdNumber, email, password, address, city, state, zip} = values;
        const organizationDetails = {
          name,
          address,
          city,
          state,
          zip,
          taxIdNumber: taxIdNumber ? taxIdNumber.toString() : '',
        };
        const data =
          socialSignup?.provider === 'Apple'
            ? await dispatch(
                organizationAppleSignup({
                  ...organizationDetails,
                  signupToken: socialSignup.signupToken,
                })
              ).unwrap()
            : socialSignup?.provider === 'Google'
            ? await dispatch(
                organizationGoogleSignup({
                  ...organizationDetails,
                  idToken: socialSignup.idToken,
                })
              ).unwrap()
            : await dispatch(organizationSignup({...organizationDetails, email, password})).unwrap();

        if (data) {
          await setNotificationSettings(data.id);
          orgStorage.set('userType', USER_TYPE.ORGANIZATION);
          nextStepHandler(activeStep + 1);
        }
      } catch (error) {
        const message = getErrorMessage(error);
        if (message === 'AppleSignupSessionInvalid') {
          setSocialSignup(null);
          toast.info(
            'Apple verification expired. Select Apple again to continue. Your organization details are saved in this form.'
          );
        } else {
          toast.error(message === 'EmailAlreadyExists' ? 'An account already exists. Please log in instead.' : message);
        }
      }
    } else if (activeStep === SIGNUP_STEP.LOGO) {
      if (!organizationId) return;

      const {image} = values;

      if (!image) {
        toast.info('Please, load the logo');
        return;
      }

      try {
        const logo = await getImage(Array.isArray(image) ? image[0] : (image as File));

        const params = {
          id: organizationId,
          email: organization?.email || '',
          name: organization?.name || '',
          address: organization?.address || '',
          taxIdNumber: organization?.taxIdNumber || '',
          phoneNumber: organization?.phoneNumber || '',
          logo: logo || '',
          city: organization?.city || '',
          state: organization?.state || '',
          zip: organization?.zip || '',
        };

        await dispatch(changeProfile(params)).unwrap();
        nextStepHandler(activeStep + 1);
      } catch (error) {
        toast.error(getErrorMessage(error));
      }
    } else if (activeStep === SIGNUP_STEP.PAYMENT) {
      if (organizationId) {
        setIsSuccessModalOpen(true);
      }
    }
  };

  return (
    <Box sx={formStepperStyle.wrapper}>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmitClickHandler}
        validationSchema={getValidationSchema(!!socialSignup)}
        validateOnMount={true}
      >
        {activeStep === SIGNUP_STEP.FORM ? (
          <SignUpFormContent
            socialProvider={socialSignup?.provider}
            socialEmail={socialSignup?.email}
            onGoogleSignupSuccess={handleGoogleSignupSuccess}
            onAppleSignupSuccess={handleAppleSignupSuccess}
            onResetSocialSignup={() => setSocialSignup(null)}
          />
        ) : activeStep === SIGNUP_STEP.LOGO ? (
          <LogoFormContent />
        ) : (
          <PaymentContent />
        )}
      </Formik>

      <Stepper steps={3} activeStep={activeStep} sx={formStepperStyle.stepper} />

      <Typography variant={BODY2} sx={formStepperStyle.email}>
        info@eventsimpli.com
      </Typography>

      <Modal
        title="Account Successfully Created"
        description="Congratulations, your account has been successfully created."
        mainIcon={<CheckIcon />}
        closeButtonTitle={DONE}
        onClose={onOpenSuccessModal}
        open={isSuccessModalOpen}
        isShowCloseButton
        closeButtonVariant={FILLED}
        sx={formStepperStyle.modal}
      />
    </Box>
  );
};

export default FormStepper;
